// src/permissions.ts
var PERMISSIONS = {
  // المنتجات والمخزون
  PRODUCT_VIEW: "product.view",
  PRODUCT_MANAGE: "product.manage",
  STOCK_VIEW: "stock.view",
  STOCK_ADJUST: "stock.adjust",
  // البيع
  SALE_CREATE: "sale.create",
  SALE_VIEW: "sale.view",
  SALE_REFUND: "sale.refund",
  SALE_DISCOUNT: "sale.discount",
  SHIFT_MANAGE: "shift.manage",
  // الطلبات
  ORDER_VIEW: "order.view",
  ORDER_MANAGE: "order.manage",
  // الشراء
  PURCHASE_VIEW: "purchase.view",
  PURCHASE_MANAGE: "purchase.manage",
  SUPPLIER_MANAGE: "supplier.manage",
  // الزبائن
  CUSTOMER_VIEW: "customer.view",
  CUSTOMER_MANAGE: "customer.manage",
  // التقارير والإعدادات
  REPORT_VIEW: "report.view",
  SETTINGS_MANAGE: "settings.manage",
  USER_MANAGE: "user.manage"
};
var ALL_PERMISSIONS = Object.values(PERMISSIONS);
var ROLE_PRESETS = {
  owner: { nameAr: "\u0627\u0644\u0645\u0627\u0644\u0643", permissions: ALL_PERMISSIONS },
  manager: {
    nameAr: "\u0645\u062F\u064A\u0631",
    permissions: ALL_PERMISSIONS.filter((p) => p !== PERMISSIONS.USER_MANAGE)
  },
  cashier: {
    nameAr: "\u0623\u0645\u064A\u0646 \u0627\u0644\u0635\u0646\u062F\u0648\u0642",
    permissions: [
      PERMISSIONS.PRODUCT_VIEW,
      PERMISSIONS.STOCK_VIEW,
      PERMISSIONS.SALE_CREATE,
      PERMISSIONS.SALE_VIEW,
      PERMISSIONS.SHIFT_MANAGE,
      PERMISSIONS.CUSTOMER_VIEW,
      PERMISSIONS.CUSTOMER_MANAGE,
      PERMISSIONS.ORDER_VIEW
    ]
  },
  stockkeeper: {
    nameAr: "\u0623\u0645\u064A\u0646 \u0627\u0644\u0645\u062E\u0632\u0646",
    permissions: [
      PERMISSIONS.PRODUCT_VIEW,
      PERMISSIONS.PRODUCT_MANAGE,
      PERMISSIONS.STOCK_VIEW,
      PERMISSIONS.STOCK_ADJUST,
      PERMISSIONS.PURCHASE_VIEW,
      PERMISSIONS.PURCHASE_MANAGE,
      PERMISSIONS.SUPPLIER_MANAGE
    ]
  }
};
function hasPermission(granted, required) {
  return granted.includes(required);
}
function hasAnyPermission(granted, required) {
  return required.some((p) => granted.includes(p));
}

// src/money.ts
var CURRENCIES = {
  DZD: { code: "DZD", nameAr: "\u062F\u064A\u0646\u0627\u0631 \u062C\u0632\u0627\u0626\u0631\u064A", symbolAr: "\u062F\u062C", decimals: 2 },
  MAD: { code: "MAD", nameAr: "\u062F\u0631\u0647\u0645 \u0645\u063A\u0631\u0628\u064A", symbolAr: "\u062F.\u0645", decimals: 2 },
  TND: { code: "TND", nameAr: "\u062F\u064A\u0646\u0627\u0631 \u062A\u0648\u0646\u0633\u064A", symbolAr: "\u062F.\u062A", decimals: 3 },
  EUR: { code: "EUR", nameAr: "\u0623\u0648\u0631\u0648", symbolAr: "\u20AC", decimals: 2 },
  USD: { code: "USD", nameAr: "\u062F\u0648\u0644\u0627\u0631", symbolAr: "$", decimals: 2 }
};
var DEFAULT_CURRENCY = "DZD";
function toMinor(amount, currency = DEFAULT_CURRENCY) {
  return Math.round(amount * 10 ** CURRENCIES[currency].decimals);
}
function toMajor(minor, currency = DEFAULT_CURRENCY) {
  return minor / 10 ** CURRENCIES[currency].decimals;
}
function calcLine(input) {
  const { unitPrice, quantity, discountPercent = 0, discountAmount = 0, taxRate = 0, taxIncluded = false } = input;
  const subtotal = Math.round(unitPrice * quantity);
  const percentPart = Math.round(subtotal * discountPercent / 100);
  const discount = Math.min(subtotal, percentPart + discountAmount);
  const afterDiscount = subtotal - discount;
  if (taxRate === 0) {
    return { subtotal, discount, taxable: afterDiscount, tax: 0, total: afterDiscount };
  }
  if (taxIncluded) {
    const taxable = Math.round(afterDiscount / (1 + taxRate / 100));
    return { subtotal, discount, taxable, tax: afterDiscount - taxable, total: afterDiscount };
  }
  const tax = Math.round(afterDiscount * taxRate / 100);
  return { subtotal, discount, taxable: afterDiscount, tax, total: afterDiscount + tax };
}
function sumTotals(lines) {
  return lines.reduce(
    (acc, l) => ({
      subtotal: acc.subtotal + l.subtotal,
      discount: acc.discount + l.discount,
      taxable: acc.taxable + l.taxable,
      tax: acc.tax + l.tax,
      total: acc.total + l.total
    }),
    { subtotal: 0, discount: 0, taxable: 0, tax: 0, total: 0 }
  );
}

// src/format.ts
var DEFAULT_LOCALE = "ar-DZ";
function localeWith(locale, digits) {
  return `${locale}-u-nu-${digits}`;
}
function formatNumber(value, { locale = DEFAULT_LOCALE, digits = "latn", ...opts } = {}) {
  return new Intl.NumberFormat(localeWith(locale, digits), opts).format(value);
}
function formatMoney(minor, {
  currency = DEFAULT_CURRENCY,
  locale = DEFAULT_LOCALE,
  digits = "latn",
  withSymbol = true
} = {}) {
  const def = CURRENCIES[currency];
  const num = new Intl.NumberFormat(localeWith(locale, digits), {
    minimumFractionDigits: def.decimals,
    maximumFractionDigits: def.decimals
  }).format(toMajor(minor, currency));
  return withSymbol ? `${num} ${def.symbolAr}` : num;
}
function formatDate(value, { locale = DEFAULT_LOCALE, digits = "latn", ...opts } = {}) {
  const d = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(localeWith(locale, digits), {
    dateStyle: opts.dateStyle ?? (opts.day || opts.month || opts.year ? void 0 : "medium"),
    ...opts
  }).format(d);
}
function formatDateTime(value, locale = DEFAULT_LOCALE, digits = "latn") {
  const d = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(localeWith(locale, digits), { dateStyle: "medium", timeStyle: "short" }).format(d);
}
var ARABIC_INDIC = "\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669";
var EASTERN_ARABIC = "\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9";
function normalizeDigits(input) {
  return input.replace(/[٠-٩۰-۹]/g, (ch) => {
    const i = ARABIC_INDIC.indexOf(ch);
    return String(i !== -1 ? i : EASTERN_ARABIC.indexOf(ch));
  });
}
function normalizeArabic(input) {
  return normalizeDigits(input).replace(/[ً-ٰٟـ]/g, "").replace(/[أإآٱ]/g, "\u0627").replace(/ى/g, "\u064A").replace(/ة/g, "\u0647").replace(/[ؤئ]/g, "\u0621").toLowerCase().trim();
}
function formatCompact(value, { locale = DEFAULT_LOCALE, digits = "latn" } = {}) {
  return new Intl.NumberFormat(localeWith(locale, digits), {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(value);
}
function formatMoneyCompact(minor, {
  currency = DEFAULT_CURRENCY,
  locale = DEFAULT_LOCALE,
  digits = "latn"
} = {}) {
  return formatCompact(toMajor(minor, currency), { locale, digits });
}

// src/schemas.ts
import { z } from "zod";
var loginSchema = z.object({
  username: z.string().trim().min(3, "\u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0642\u0635\u064A\u0631 \u062C\u062F\u0627\u064B").max(64),
  password: z.string().min(6, "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u0642\u0644 \u0639\u0646 \u0666 \u0623\u062D\u0631\u0641").max(128),
  deviceId: z.string().max(128).optional()
});
var refreshSchema = z.object({ refreshToken: z.string().min(10) });
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "\u0623\u062F\u062E\u0644 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062D\u0627\u0644\u064A\u0629"),
  newPassword: z.string().min(8, "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062C\u062F\u064A\u062F\u0629 \u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u0642\u0644 \u0639\u0646 \u0668 \u0623\u062D\u0631\u0641").max(128),
  confirmPassword: z.string()
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "\u0643\u0644\u0645\u062A\u0627 \u0627\u0644\u0645\u0631\u0648\u0631 \u063A\u064A\u0631 \u0645\u062A\u0637\u0627\u0628\u0642\u062A\u064A\u0646",
  path: ["confirmPassword"]
});
var createUserSchema = z.object({
  username: z.string().trim().min(3, "\u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0642\u0635\u064A\u0631 \u062C\u062F\u0627\u064B").max(64).regex(/^[a-zA-Z0-9._-]+$/, "\u0623\u062D\u0631\u0641 \u0644\u0627\u062A\u064A\u0646\u064A\u0629 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648 . _ - \u0641\u0642\u0637"),
  fullName: z.string().trim().min(2, "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0643\u0627\u0645\u0644 \u0645\u0637\u0644\u0648\u0628").max(120),
  password: z.string().min(8, "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u0642\u0644 \u0639\u0646 \u0668 \u0623\u062D\u0631\u0641").max(128),
  phone: z.string().trim().max(32).optional().or(z.literal("")),
  roleId: z.string().uuid("\u0627\u062E\u062A\u0631 \u062F\u0648\u0631\u0627\u064B"),
  storeId: z.string().uuid().optional(),
  isActive: z.boolean().default(true)
});
var updateUserSchema = createUserSchema.partial().omit({ password: true });
var permissionSchema = z.enum(ALL_PERMISSIONS);
var createRoleSchema = z.object({
  key: z.string().trim().min(2).max(48).regex(/^[a-z0-9_]+$/, "\u0623\u062D\u0631\u0641 \u0635\u063A\u064A\u0631\u0629 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648 _ \u0641\u0642\u0637"),
  nameAr: z.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u062F\u0648\u0631 \u0645\u0637\u0644\u0648\u0628").max(80),
  permissions: z.array(permissionSchema).min(1, "\u0627\u062E\u062A\u0631 \u0635\u0644\u0627\u062D\u064A\u0629 \u0648\u0627\u062D\u062F\u0629 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644")
});
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().max(200).optional(),
  sortBy: z.string().max(64).optional(),
  sortDir: z.enum(["asc", "desc"]).default("desc")
});

// src/catalog.ts
import { z as z2 } from "zod";
var PRODUCT_TYPES = {
  BOOK: "\u0643\u062A\u0627\u0628",
  STATIONERY: "\u0642\u0631\u0637\u0627\u0633\u064A\u0629",
  SERVICE: "\u062E\u062F\u0645\u0629",
  OTHER: "\u0623\u062E\u0631\u0649"
};
var MOVEMENT_REASONS = {
  INITIAL: "\u0631\u0635\u064A\u062F \u0627\u0641\u062A\u062A\u0627\u062D\u064A",
  PURCHASE: "\u0634\u0631\u0627\u0621",
  SALE: "\u0628\u064A\u0639",
  RETURN_IN: "\u0625\u0631\u062C\u0627\u0639 \u0645\u0646 \u0632\u0628\u0648\u0646",
  RETURN_OUT: "\u0625\u0631\u062C\u0627\u0639 \u0625\u0644\u0649 \u0645\u0648\u0631\u0651\u062F",
  ADJUSTMENT: "\u062A\u0633\u0648\u064A\u0629 \u062C\u0631\u062F",
  DAMAGE: "\u062A\u0644\u0641 \u0623\u0648 \u0641\u0642\u062F\u0627\u0646",
  TRANSFER_IN: "\u062A\u062D\u0648\u064A\u0644 \u0648\u0627\u0631\u062F",
  TRANSFER_OUT: "\u062A\u062D\u0648\u064A\u0644 \u0635\u0627\u062F\u0631"
};
var MANUAL_MOVEMENT_REASONS = [
  "INITIAL",
  "ADJUSTMENT",
  "DAMAGE",
  "RETURN_IN",
  "RETURN_OUT",
  "TRANSFER_IN",
  "TRANSFER_OUT"
];
var UNITS = ["\u0642\u0637\u0639\u0629", "\u0639\u0644\u0628\u0629", "\u0631\u0632\u0645\u0629", "\u0643\u0631\u062A\u0648\u0646", "\u062F\u0632\u064A\u0646\u0629", "\u0645\u062A\u0631", "\u0643\u064A\u0644\u0648\u063A\u0631\u0627\u0645", "\u0648\u0631\u0642\u0629"];
var TAX_RATES = [0, 9, 19];
var categorySchema = z2.object({
  nameAr: z2.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u062A\u0635\u0646\u064A\u0641 \u0645\u0637\u0644\u0648\u0628").max(120),
  parentId: z2.uuid().nullable().optional(),
  icon: z2.string().trim().max(48).nullable().optional(),
  sortOrder: z2.coerce.number().int().min(0).max(9999).default(0),
  isActive: z2.boolean().default(true)
});
var authorSchema = z2.object({
  nameAr: z2.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0645\u0624\u0644\u0651\u0641 \u0645\u0637\u0644\u0648\u0628").max(160),
  nameFr: z2.string().trim().max(160).nullable().optional().or(z2.literal(""))
});
var publisherSchema = z2.object({
  nameAr: z2.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0646\u0627\u0634\u0631 \u0645\u0637\u0644\u0648\u0628").max(160),
  nameFr: z2.string().trim().max(160).nullable().optional().or(z2.literal("")),
  country: z2.string().trim().max(80).nullable().optional().or(z2.literal(""))
});
var priceField = (label) => z2.coerce.number({ error: `${label} \u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0631\u0642\u0645\u0627\u064B` }).int().min(0, `${label} \u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0633\u0627\u0644\u0628\u0627\u064B`).max(1e8);
var bookDetailSchema = z2.object({
  isbn: z2.string().trim().max(20).nullable().optional().or(z2.literal("")),
  authorId: z2.uuid().nullable().optional(),
  publisherId: z2.uuid().nullable().optional(),
  pages: z2.coerce.number().int().min(1).max(2e4).nullable().optional(),
  language: z2.string().trim().max(40).nullable().optional().or(z2.literal("")),
  edition: z2.string().trim().max(60).nullable().optional().or(z2.literal("")),
  publishYear: z2.coerce.number().int().min(1400).max(2200).nullable().optional()
});
var productBaseSchema = z2.object({
  sku: z2.string().trim().min(2, "\u0627\u0644\u0631\u0645\u0632 \u0645\u0637\u0644\u0648\u0628").max(40).regex(/^[A-Za-z0-9\-_.]+$/, "\u0623\u062D\u0631\u0641 \u0644\u0627\u062A\u064A\u0646\u064A\u0629 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648 - _ . \u0641\u0642\u0637"),
  barcode: z2.string().trim().max(64).regex(/^[0-9A-Za-z\-]*$/, "\u0628\u0627\u0631\u0643\u0648\u062F \u063A\u064A\u0631 \u0635\u0627\u0644\u062D").nullable().optional().or(z2.literal("")),
  nameAr: z2.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0645\u0646\u062A\u062C \u0645\u0637\u0644\u0648\u0628").max(200),
  nameFr: z2.string().trim().max(200).nullable().optional().or(z2.literal("")),
  description: z2.string().trim().max(1e3).nullable().optional().or(z2.literal("")),
  type: z2.enum(["BOOK", "STATIONERY", "SERVICE", "OTHER"]).default("STATIONERY"),
  categoryId: z2.uuid("\u0627\u062E\u062A\u0631 \u062A\u0635\u0646\u064A\u0641\u0627\u064B").nullable().optional(),
  unit: z2.string().trim().min(1).max(32).default("\u0642\u0637\u0639\u0629"),
  costPrice: priceField("\u0633\u0639\u0631 \u0627\u0644\u062A\u0643\u0644\u0641\u0629").default(0),
  retailPrice: priceField("\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639").default(0),
  wholesalePrice: priceField("\u0633\u0639\u0631 \u0627\u0644\u062C\u0645\u0644\u0629").nullable().optional(),
  taxRate: z2.coerce.number().int().min(0).max(100).default(0),
  minStock: z2.coerce.number().int().min(0).max(1e6).default(0),
  trackStock: z2.boolean().default(true),
  images: z2.array(z2.string().trim().max(500)).max(8).default([]),
  isActive: z2.boolean().default(true),
  bookDetail: bookDetailSchema.nullable().optional()
});
function applyProductRules(schema) {
  return schema.refine((p) => p.type !== "BOOK" || !!p.bookDetail, {
    message: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0643\u062A\u0627\u0628 \u0645\u0637\u0644\u0648\u0628\u0629",
    path: ["bookDetail"]
  }).refine((p) => p.type !== "SERVICE" || !p.trackStock, {
    message: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0644\u0627 \u064A\u0645\u0643\u0646 \u062A\u062A\u0628\u0651\u0639 \u0645\u062E\u0632\u0648\u0646\u0647\u0627",
    path: ["trackStock"]
  }).refine((p) => p.retailPrice >= p.costPrice || p.retailPrice === 0, {
    message: "\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639 \u0623\u0642\u0644 \u0645\u0646 \u0627\u0644\u062A\u0643\u0644\u0641\u0629 \u2014 \u062A\u062D\u0642\u0651\u0642 \u0645\u0646 \u0627\u0644\u0633\u0639\u0631\u064A\u0646",
    path: ["retailPrice"]
  });
}
var productSchema = applyProductRules(productBaseSchema);
var createProductSchema = applyProductRules(
  productBaseSchema.extend({
    openingQty: z2.coerce.number().int().min(0).max(1e6).default(0)
  })
);
var productFilterSchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(200).default(25),
  search: z2.string().trim().max(200).optional(),
  categoryId: z2.uuid().optional(),
  type: z2.enum(["BOOK", "STATIONERY", "SERVICE", "OTHER"]).optional(),
  /** all = الكل · low = وصل حدّ التنبيه · out = نفد · in = متوفّر */
  stock: z2.enum(["all", "low", "out", "in"]).default("all"),
  isActive: z2.enum(["all", "yes", "no"]).default("all"),
  sortBy: z2.enum(["createdAt", "nameAr", "retailPrice", "quantity", "sku"]).default("createdAt"),
  sortDir: z2.enum(["asc", "desc"]).default("desc")
});
var stockAdjustmentSchema = z2.object({
  productId: z2.uuid("\u0627\u062E\u062A\u0631 \u0645\u0646\u062A\u062C\u0627\u064B"),
  storeId: z2.uuid().optional(),
  /** موجب = إدخال، سالب = إخراج. الصفر بلا معنى في سجل تراكمي. */
  qtyDelta: z2.coerce.number({ error: "\u0627\u0644\u0643\u0645\u064A\u0629 \u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0631\u0642\u0645\u0627\u064B" }).int().refine((n) => n !== 0, "\u0627\u0644\u0643\u0645\u064A\u0629 \u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0635\u0641\u0631\u0627\u064B"),
  reason: z2.enum(MANUAL_MOVEMENT_REASONS),
  unitCost: z2.coerce.number().int().min(0).nullable().optional(),
  note: z2.string().trim().max(255).optional().or(z2.literal(""))
});
var stockCountSchema = z2.object({
  productId: z2.uuid(),
  storeId: z2.uuid().optional(),
  countedQty: z2.coerce.number().int().min(0, "\u0627\u0644\u0639\u062F\u062F \u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0633\u0627\u0644\u0628\u0627\u064B"),
  note: z2.string().trim().max(255).optional().or(z2.literal(""))
});
var movementFilterSchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(200).default(50),
  productId: z2.uuid().optional(),
  storeId: z2.uuid().optional(),
  reason: z2.enum(Object.keys(MOVEMENT_REASONS)).optional(),
  from: z2.string().optional(),
  to: z2.string().optional()
});
function buildSearchKey(...parts) {
  return normalizeArabic(parts.filter(Boolean).join(" ")).slice(0, 240);
}
function suggestSku(type, seed = Math.random()) {
  const prefix = { BOOK: "KTB", STATIONERY: "QRT", SERVICE: "KHD", OTHER: "GEN" }[type];
  const rand = Math.floor(seed * 65535).toString(16).toUpperCase().padStart(4, "0");
  return `NAJ-${prefix}-${rand}`;
}
function stockStatus(quantity, minStock) {
  if (quantity <= 0) return "out";
  if (minStock > 0 && quantity <= minStock) return "low";
  return "ok";
}
function profitMargin(costPrice, retailPrice) {
  if (costPrice <= 0) return null;
  return (retailPrice - costPrice) / costPrice * 100;
}

// src/sales.ts
import { z as z3 } from "zod";
var PAYMENT_METHODS = {
  CASH: "\u0646\u0642\u062F\u0627\u064B",
  CARD: "\u0628\u0637\u0627\u0642\u0629",
  TRANSFER: "\u062A\u062D\u0648\u064A\u0644",
  CREDIT: "\u0622\u062C\u0644 (\u062F\u064A\u0646)"
};
var SALE_STATUSES = {
  COMPLETED: "\u0645\u0643\u062A\u0645\u0644\u0629",
  PARTIALLY_REFUNDED: "\u0645\u0631\u062A\u062C\u0639 \u062C\u0632\u0626\u064A",
  REFUNDED: "\u0645\u0631\u062A\u062C\u0639\u0629",
  HELD: "\u0645\u0639\u0644\u0651\u0642\u0629",
  VOIDED: "\u0645\u0644\u063A\u0627\u0629"
};
var SHIFT_STATUSES = { OPEN: "\u0645\u0641\u062A\u0648\u062D\u0629", CLOSED: "\u0645\u063A\u0644\u0642\u0629" };
var CASH_MOVEMENT_TYPES = {
  OPENING: "\u0631\u0635\u064A\u062F \u0627\u0641\u062A\u062A\u0627\u062D\u064A",
  SALE: "\u0645\u0642\u0628\u0648\u0636\u0627\u062A \u0628\u064A\u0639",
  REFUND: "\u0645\u0631\u062A\u062C\u0639",
  DEPOSIT: "\u0625\u064A\u062F\u0627\u0639",
  WITHDRAWAL: "\u0633\u062D\u0628",
  CLOSING: "\u0625\u063A\u0644\u0627\u0642"
};
var saleItemSchema = z3.object({
  id: z3.uuid(),
  productId: z3.uuid(),
  nameSnapshot: z3.string().trim().min(1).max(200),
  skuSnapshot: z3.string().trim().min(1).max(40),
  unitSnapshot: z3.string().trim().max(32).default("\u0642\u0637\u0639\u0629"),
  costSnapshot: z3.coerce.number().int().min(0).default(0),
  unitPrice: z3.coerce.number().int().min(0),
  quantity: z3.coerce.number().int().min(1, "\u0627\u0644\u0643\u0645\u064A\u0629 \u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0661 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644").max(1e5),
  discount: z3.coerce.number().int().min(0).default(0),
  taxRate: z3.coerce.number().int().min(0).max(100).default(0)
});
var salePaymentSchema = z3.object({
  id: z3.uuid(),
  method: z3.enum(["CASH", "CARD", "TRANSFER", "CREDIT"]),
  amount: z3.coerce.number().int().min(1, "\u0627\u0644\u0645\u0628\u0644\u063A \u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0623\u0643\u0628\u0631 \u0645\u0646 \u0635\u0641\u0631"),
  reference: z3.string().trim().max(64).optional().or(z3.literal(""))
});
var saleSchema = z3.object({
  /** يُولَّد على الجهاز — يجعل إعادة إرسال نفس الفاتورة غير مؤذية */
  id: z3.uuid(),
  reference: z3.string().trim().min(3).max(48),
  items: z3.array(saleItemSchema).min(1, "\u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0641\u0627\u0631\u063A\u0629 \u2014 \u0623\u0636\u0641 \u0645\u0646\u062A\u062C\u0627\u064B \u0648\u0627\u062D\u062F\u0627\u064B \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644"),
  payments: z3.array(salePaymentSchema).min(1, "\u0623\u0636\u0641 \u0637\u0631\u064A\u0642\u0629 \u062F\u0641\u0639 \u0648\u0627\u062D\u062F\u0629 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644"),
  storeId: z3.uuid().optional(),
  registerId: z3.uuid().nullable().optional(),
  shiftId: z3.uuid().nullable().optional(),
  /** مطلوب عند وجود دفعة آجلة */
  customerId: z3.uuid().nullable().optional(),
  customerName: z3.string().trim().max(120).optional().or(z3.literal("")),
  customerPhone: z3.string().trim().max(32).optional().or(z3.literal("")),
  note: z3.string().trim().max(255).optional().or(z3.literal("")),
  deviceKey: z3.string().max(128).optional(),
  occurredAt: z3.string(),
  /** ما حسبه الجهاز — يُقارَن بحساب الخادم، والاختلاف خطأ صريح لا تجاهل صامت */
  total: z3.coerce.number().int().min(0)
}).refine(
  (s) => s.payments.reduce((sum, p) => sum + p.amount, 0) >= s.total,
  { message: "\u0627\u0644\u0645\u0628\u0644\u063A \u0627\u0644\u0645\u062F\u0641\u0648\u0639 \u0623\u0642\u0644 \u0645\u0646 \u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629", path: ["payments"] }
).refine(
  (s) => !s.payments.some((p) => p.method === "CREDIT") || !!s.customerId,
  { message: "\u0627\u0644\u0628\u064A\u0639 \u0627\u0644\u0622\u062C\u0644 \u064A\u062A\u0637\u0644\u0651\u0628 \u0627\u062E\u062A\u064A\u0627\u0631 \u0632\u0628\u0648\u0646 \u2014 \u0627\u0644\u062F\u064A\u0646 \u0644\u0627 \u064A\u064F\u0633\u062C\u064E\u0651\u0644 \u0628\u0644\u0627 \u0635\u0627\u062D\u0628", path: ["customerId"] }
).refine(
  (s) => {
    const paid = s.payments.reduce((sum, p) => sum + p.amount, 0);
    const cash = s.payments.filter((p) => p.method === "CASH").reduce((sum, p) => sum + p.amount, 0);
    return paid - s.total <= cash;
  },
  { message: "\u0627\u0644\u0641\u0627\u0626\u0636 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0645\u062F\u0641\u0648\u0639 \u0646\u0642\u062F\u0627\u064B \u2014 \u0644\u0627 \u064A\u0645\u0643\u0646 \u0631\u062F\u0651 \u0627\u0644\u0628\u0627\u0642\u064A", path: ["payments"] }
);
function calcSaleTotals(items) {
  const lines = items.map(
    (item) => calcLine({
      unitPrice: item.unitPrice,
      quantity: item.quantity,
      discountAmount: item.discount,
      taxRate: item.taxRate,
      taxIncluded: false
    })
  );
  const summed = sumTotals(lines);
  return {
    subtotal: summed.subtotal,
    discountTotal: summed.discount,
    taxTotal: summed.tax,
    total: summed.total
  };
}
function calcSaleLine(item) {
  return calcLine({
    unitPrice: item.unitPrice,
    quantity: item.quantity,
    discountAmount: item.discount,
    taxRate: item.taxRate,
    taxIncluded: false
  });
}
function calcChange(payments, total) {
  const paid = payments.reduce((sum, p) => sum + p.amount, 0);
  return Math.max(0, paid - total);
}
var openShiftSchema = z3.object({
  registerId: z3.uuid("\u0627\u062E\u062A\u0631 \u0635\u0646\u062F\u0648\u0642\u0627\u064B"),
  openingCash: z3.coerce.number().int().min(0, "\u0627\u0644\u0631\u0635\u064A\u062F \u0627\u0644\u0627\u0641\u062A\u062A\u0627\u062D\u064A \u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0633\u0627\u0644\u0628\u0627\u064B").default(0),
  note: z3.string().trim().max(255).optional().or(z3.literal(""))
});
var closeShiftSchema = z3.object({
  countedCash: z3.coerce.number().int().min(0, "\u0627\u0644\u0645\u0628\u0644\u063A \u0627\u0644\u0645\u0639\u062F\u0648\u062F \u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0633\u0627\u0644\u0628\u0627\u064B"),
  note: z3.string().trim().max(255).optional().or(z3.literal(""))
});
var cashMovementSchema = z3.object({
  type: z3.enum(["DEPOSIT", "WITHDRAWAL"]),
  amount: z3.coerce.number().int().min(1, "\u0627\u0644\u0645\u0628\u0644\u063A \u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0623\u0643\u0628\u0631 \u0645\u0646 \u0635\u0641\u0631"),
  note: z3.string().trim().max(255).optional().or(z3.literal(""))
});
var saleReturnSchema = z3.object({
  id: z3.uuid(),
  reference: z3.string().trim().min(3).max(48),
  saleId: z3.uuid(),
  items: z3.array(
    z3.object({
      saleItemId: z3.uuid(),
      quantity: z3.coerce.number().int().min(1, "\u0627\u0644\u0643\u0645\u064A\u0629 \u0627\u0644\u0645\u064F\u0631\u062C\u064E\u0639\u0629 \u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0661 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644")
    })
  ).min(1, "\u0627\u062E\u062A\u0631 \u0633\u0637\u0631\u0627\u064B \u0648\u0627\u062D\u062F\u0627\u064B \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644 \u0644\u0644\u0625\u0631\u062C\u0627\u0639"),
  reason: z3.string().trim().max(255).optional().or(z3.literal("")),
  deviceKey: z3.string().max(128).optional(),
  occurredAt: z3.string()
});
function buildSaleReference(storeCode, deviceCode, date, counter) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${storeCode}-${deviceCode}-${y}${m}${d}-${String(counter).padStart(4, "0")}`;
}

// src/purchasing.ts
import { z as z4 } from "zod";
var PURCHASE_STATUSES = {
  DRAFT: "\u0645\u0633\u0648\u0651\u062F\u0629",
  RECEIVED: "\u0645\u064F\u0633\u062A\u0644\u064E\u0645\u0629",
  CANCELLED: "\u0645\u0644\u063A\u0627\u0629"
};
var PAYMENT_DIRECTIONS = {
  OUT: "\u062F\u0641\u0639\u0629 \u0644\u0644\u0645\u0648\u0631\u0651\u062F",
  IN: "\u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0645\u0646 \u0627\u0644\u0645\u0648\u0631\u0651\u062F"
};
var supplierSchema = z4.object({
  code: z4.string().trim().min(2, "\u0627\u0644\u0631\u0645\u0632 \u0645\u0637\u0644\u0648\u0628").max(24).regex(/^[A-Za-z0-9\-_.]+$/, "\u0623\u062D\u0631\u0641 \u0644\u0627\u062A\u064A\u0646\u064A\u0629 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648 - _ . \u0641\u0642\u0637"),
  nameAr: z4.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0645\u0648\u0631\u0651\u062F \u0645\u0637\u0644\u0648\u0628").max(160),
  contactName: z4.string().trim().max(120).optional().or(z4.literal("")),
  phone: z4.string().trim().max(32).optional().or(z4.literal("")),
  email: z4.email("\u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u063A\u064A\u0631 \u0635\u0627\u0644\u062D").max(160).optional().or(z4.literal("")),
  address: z4.string().trim().max(255).optional().or(z4.literal("")),
  notes: z4.string().trim().max(1e3).optional().or(z4.literal("")),
  openingBalance: z4.coerce.number().int().min(0).default(0),
  isActive: z4.boolean().default(true)
});
var purchaseItemSchema = z4.object({
  id: z4.uuid().optional(),
  productId: z4.uuid("\u0627\u062E\u062A\u0631 \u0645\u0646\u062A\u062C\u0627\u064B"),
  unitCost: z4.coerce.number().int().min(0, "\u0627\u0644\u062A\u0643\u0644\u0641\u0629 \u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0633\u0627\u0644\u0628\u0629"),
  quantity: z4.coerce.number().int().min(1, "\u0627\u0644\u0643\u0645\u064A\u0629 \u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0661 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644").max(1e6),
  discount: z4.coerce.number().int().min(0).default(0),
  taxRate: z4.coerce.number().int().min(0).max(100).default(0),
  /** سعر بيع جديد يُطبَّق على المنتج عند الاستلام */
  newRetailPrice: z4.coerce.number().int().min(0).nullable().optional()
});
var purchaseSchema = z4.object({
  supplierId: z4.uuid("\u0627\u062E\u062A\u0631 \u0645\u0648\u0631\u0651\u062F\u0627\u064B"),
  invoiceNumber: z4.string().trim().max(64).optional().or(z4.literal("")),
  invoiceDate: z4.string().optional().or(z4.literal("")),
  items: z4.array(purchaseItemSchema).min(1, "\u0623\u0636\u0641 \u0635\u0646\u0641\u0627\u064B \u0648\u0627\u062D\u062F\u0627\u064B \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644"),
  /** مصاريف نقل تُوزَّع على الأصناف لحساب التكلفة الحقيقية */
  shippingCost: z4.coerce.number().int().min(0).default(0),
  discountTotal: z4.coerce.number().int().min(0).default(0),
  note: z4.string().trim().max(500).optional().or(z4.literal("")),
  /** RECEIVED يُدخل البضاعة للمخزون فوراً · DRAFT يؤجّلها */
  status: z4.enum(["DRAFT", "RECEIVED"]).default("RECEIVED")
});
function calcPurchaseTotals(items, shippingCost = 0, invoiceDiscount = 0) {
  const lines = items.map(
    (item) => calcLine({
      unitPrice: item.unitCost,
      quantity: item.quantity,
      discountAmount: item.discount,
      taxRate: item.taxRate,
      taxIncluded: false
    })
  );
  const summed = sumTotals(lines);
  return {
    subtotal: summed.subtotal,
    itemDiscount: summed.discount,
    taxTotal: summed.tax,
    total: Math.max(0, summed.total + shippingCost - invoiceDiscount)
  };
}
function allocateShipping(lineValues, shippingCost) {
  if (shippingCost <= 0 || lineValues.length === 0) return lineValues.map(() => 0);
  const totalValue = lineValues.reduce((a, b) => a + b, 0);
  if (totalValue <= 0) {
    const each = Math.floor(shippingCost / lineValues.length);
    const shares2 = lineValues.map(() => each);
    shares2[0] = (shares2[0] ?? 0) + (shippingCost - each * lineValues.length);
    return shares2;
  }
  const shares = lineValues.map((value) => Math.floor(value * shippingCost / totalValue));
  const allocated = shares.reduce((a, b) => a + b, 0);
  const remainder = shippingCost - allocated;
  if (remainder !== 0) {
    let largest = 0;
    for (let i = 1; i < lineValues.length; i++) {
      if ((lineValues[i] ?? 0) > (lineValues[largest] ?? 0)) largest = i;
    }
    shares[largest] = (shares[largest] ?? 0) + remainder;
  }
  return shares;
}
function landedUnitCost(unitCost, quantity, lineDiscount, shippingShare) {
  if (quantity <= 0) return unitCost;
  const lineCost = unitCost * quantity - lineDiscount + shippingShare;
  return Math.round(lineCost / quantity);
}
function weightedAverageCost(currentQty, currentCost, incomingQty, incomingCost) {
  if (currentQty <= 0) return incomingCost;
  if (incomingQty <= 0) return currentCost;
  const totalValue = currentQty * currentCost + incomingQty * incomingCost;
  return Math.round(totalValue / (currentQty + incomingQty));
}
var supplierPaymentSchema = z4.object({
  supplierId: z4.uuid("\u0627\u062E\u062A\u0631 \u0645\u0648\u0631\u0651\u062F\u0627\u064B"),
  purchaseId: z4.uuid().nullable().optional(),
  direction: z4.enum(["OUT", "IN"]).default("OUT"),
  method: z4.enum(["CASH", "CARD", "TRANSFER", "CREDIT"]).default("CASH"),
  amount: z4.coerce.number().int().min(1, "\u0627\u0644\u0645\u0628\u0644\u063A \u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0623\u0643\u0628\u0631 \u0645\u0646 \u0635\u0641\u0631"),
  docNumber: z4.string().trim().max(64).optional().or(z4.literal("")),
  note: z4.string().trim().max(255).optional().or(z4.literal("")),
  /** خصم المبلغ من درج الصندوق إن كان نقداً ووردية مفتوحة */
  fromDrawer: z4.boolean().default(false),
  paidAt: z4.string().optional()
});
var expenseSchema = z4.object({
  categoryId: z4.uuid().nullable().optional(),
  amount: z4.coerce.number().int().min(1, "\u0627\u0644\u0645\u0628\u0644\u063A \u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0623\u0643\u0628\u0631 \u0645\u0646 \u0635\u0641\u0631"),
  method: z4.enum(["CASH", "CARD", "TRANSFER", "CREDIT"]).default("CASH"),
  docNumber: z4.string().trim().max(64).optional().or(z4.literal("")),
  note: z4.string().trim().max(500).optional().or(z4.literal("")),
  fromDrawer: z4.boolean().default(false),
  spentAt: z4.string().optional()
});
var expenseCategorySchema = z4.object({
  nameAr: z4.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0628\u0646\u062F \u0645\u0637\u0644\u0648\u0628").max(80),
  icon: z4.string().trim().max(48).optional().or(z4.literal("")),
  sortOrder: z4.coerce.number().int().min(0).default(0)
});

// src/orders.ts
import { z as z5 } from "zod";
var ORDER_STATUSES = {
  PENDING: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629",
  CONFIRMED: "\u0645\u0624\u0643\u064E\u0651\u062F",
  PREPARING: "\u0642\u064A\u062F \u0627\u0644\u062A\u062C\u0647\u064A\u0632",
  READY: "\u062C\u0627\u0647\u0632",
  OUT_FOR_DELIVERY: "\u0641\u064A \u0627\u0644\u0637\u0631\u064A\u0642",
  DELIVERED: "\u062A\u0645\u0651 \u0627\u0644\u062A\u0633\u0644\u064A\u0645",
  CANCELLED: "\u0645\u0644\u063A\u0649",
  REJECTED: "\u0645\u0631\u0641\u0648\u0636"
};
var FULFILLMENT_TYPES = {
  PICKUP: "\u0627\u0633\u062A\u0644\u0627\u0645 \u0645\u0646 \u0627\u0644\u0645\u0643\u062A\u0628\u0629",
  DELIVERY: "\u062A\u0648\u0635\u064A\u0644"
};
var ORDER_TRANSITIONS = {
  PENDING: ["CONFIRMED", "REJECTED", "CANCELLED"],
  CONFIRMED: ["PREPARING", "CANCELLED"],
  PREPARING: ["READY", "CANCELLED"],
  READY: ["OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"],
  OUT_FOR_DELIVERY: ["DELIVERED", "CANCELLED"],
  DELIVERED: [],
  CANCELLED: [],
  REJECTED: []
};
function canTransition(from, to) {
  return ORDER_TRANSITIONS[from].includes(to);
}
function isTerminal(status) {
  return ORDER_TRANSITIONS[status].length === 0;
}
function customerCanCancel(status) {
  return status === "PENDING" || status === "CONFIRMED";
}
var phoneField = z5.string().trim().min(9, "\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641 \u0642\u0635\u064A\u0631 \u062C\u062F\u0627\u064B").max(32).regex(/^[0-9+\s-]+$/, "\u0631\u0642\u0645 \u0647\u0627\u062A\u0641 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D");
var customerRegisterSchema = z5.object({
  phone: phoneField,
  fullName: z5.string().trim().min(3, "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0643\u0627\u0645\u0644 \u0645\u0637\u0644\u0648\u0628").max(120),
  password: z5.string().min(6, "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u0642\u0644 \u0639\u0646 \u0666 \u0623\u062D\u0631\u0641").max(128),
  email: z5.email("\u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u063A\u064A\u0631 \u0635\u0627\u0644\u062D").max(160).optional().or(z5.literal(""))
});
var customerLoginSchema = z5.object({
  phone: phoneField,
  password: z5.string().min(1, "\u0623\u062F\u062E\u0644 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631").max(128),
  deviceId: z5.string().max(128).optional()
});
var addressSchema = z5.object({
  label: z5.string().trim().min(2, "\u0633\u0645\u0650\u0651 \u0627\u0644\u0639\u0646\u0648\u0627\u0646 (\u0627\u0644\u0645\u0646\u0632\u0644\u060C \u0627\u0644\u0639\u0645\u0644\u2026)").max(60),
  wilaya: z5.string().trim().min(2, "\u0627\u0644\u0648\u0644\u0627\u064A\u0629 \u0645\u0637\u0644\u0648\u0628\u0629").max(60),
  commune: z5.string().trim().min(2, "\u0627\u0644\u0628\u0644\u062F\u064A\u0629 \u0645\u0637\u0644\u0648\u0628\u0629").max(80),
  street: z5.string().trim().min(3, "\u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0645\u0637\u0644\u0648\u0628").max(255),
  landmark: z5.string().trim().max(160).optional().or(z5.literal("")),
  phone: z5.string().trim().max(32).optional().or(z5.literal("")),
  notes: z5.string().trim().max(255).optional().or(z5.literal("")),
  isDefault: z5.boolean().default(false)
});
function formatAddress(a) {
  return [a.street, a.landmark, a.commune, a.wilaya].filter(Boolean).join("\u060C ");
}
var orderItemSchema = z5.object({
  productId: z5.uuid(),
  quantity: z5.coerce.number().int().min(1, "\u0627\u0644\u0643\u0645\u064A\u0629 \u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0661 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644").max(1e3)
});
var placeOrderSchema = z5.object({
  items: z5.array(orderItemSchema).min(1, "\u0627\u0644\u0633\u0644\u0629 \u0641\u0627\u0631\u063A\u0629"),
  fulfillment: z5.enum(["PICKUP", "DELIVERY"]).default("PICKUP"),
  addressId: z5.uuid().nullable().optional(),
  customerNote: z5.string().trim().max(500).optional().or(z5.literal(""))
}).refine((o) => o.fulfillment !== "DELIVERY" || !!o.addressId, {
  message: "\u0627\u062E\u062A\u0631 \u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u062A\u0648\u0635\u064A\u0644",
  path: ["addressId"]
});
var updateOrderStatusSchema = z5.object({
  status: z5.enum([
    "PENDING",
    "CONFIRMED",
    "PREPARING",
    "READY",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
    "CANCELLED",
    "REJECTED"
  ]),
  note: z5.string().trim().max(500).optional().or(z5.literal(""))
});
function calcDeliveryFee(fulfillment, subtotal, zone) {
  if (fulfillment === "PICKUP") return 0;
  if (!zone) return 0;
  if (zone.freeAbove > 0 && subtotal >= zone.freeAbove) return 0;
  return zone.fee;
}
var deliveryZoneSchema = z5.object({
  wilaya: z5.string().trim().min(2).max(60),
  commune: z5.string().trim().min(2).max(80),
  fee: z5.coerce.number().int().min(0).default(0),
  freeAbove: z5.coerce.number().int().min(0).default(0),
  isActive: z5.boolean().default(true)
});
function buildOrderReference(date, counter) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `ORD-${y}${m}${d}-${String(counter).padStart(4, "0")}`;
}

// src/reports.ts
import { z as z6 } from "zod";
var REPORT_PERIODS = {
  today: "\u0627\u0644\u064A\u0648\u0645",
  yesterday: "\u0623\u0645\u0633",
  week: "\u0622\u062E\u0631 \u0667 \u0623\u064A\u0627\u0645",
  month: "\u0647\u0630\u0627 \u0627\u0644\u0634\u0647\u0631",
  prev_month: "\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0645\u0627\u0636\u064A",
  year: "\u0647\u0630\u0647 \u0627\u0644\u0633\u0646\u0629",
  custom: "\u0641\u062A\u0631\u0629 \u0645\u062E\u0635\u0651\u0635\u0629"
};
var GRANULARITIES = { day: "\u064A\u0648\u0645\u064A", week: "\u0623\u0633\u0628\u0648\u0639\u064A", month: "\u0634\u0647\u0631\u064A" };
var reportRangeSchema = z6.object({
  period: z6.enum(Object.keys(REPORT_PERIODS)).default("month"),
  from: z6.string().optional(),
  to: z6.string().optional(),
  granularity: z6.enum(["day", "week", "month"]).default("day")
});
function resolveRange(range, now = /* @__PURE__ */ new Date()) {
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  switch (range.period) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case "week": {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { from: startOfDay(start), to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "prev_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(first), to: endOfDay(last) };
    }
    case "year":
      return { from: startOfDay(new Date(now.getFullYear(), 0, 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: range.from ? startOfDay(new Date(range.from)) : startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: range.to ? endOfDay(new Date(range.to)) : endOfDay(now)
      };
  }
}
function marginOf(revenue, profit) {
  if (revenue <= 0) return null;
  return profit / revenue * 100;
}
function bucketLabel(bucket, granularity, locale = "ar-DZ") {
  if (granularity === "month") {
    const [y2, m2] = bucket.split("-");
    return new Intl.DateTimeFormat(`${locale}-u-nu-latn`, { month: "long", year: "numeric" }).format(new Date(Number(y2), Number(m2) - 1, 1));
  }
  if (granularity === "week") {
    const [, w] = bucket.split("-W");
    return `\u0627\u0644\u0623\u0633\u0628\u0648\u0639 ${w}`;
  }
  const [y, m, d] = bucket.split("-").map(Number);
  return new Intl.DateTimeFormat(`${locale}-u-nu-latn`, { day: "numeric", month: "short" }).format(new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}
function toCsv(rows, headers) {
  const keys = Object.keys(headers);
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    keys.map((k) => escape(headers[k] ?? k)).join(","),
    ...rows.map((r) => keys.map((k) => escape(r[k] ?? "")).join(","))
  ];
  return "\uFEFF" + lines.join("\r\n");
}

// src/import.ts
import { z as z7 } from "zod";
function detectEncoding(bytes) {
  if (bytes.length >= 3 && bytes[0] === 239 && bytes[1] === 187 && bytes[2] === 191) {
    return "utf-8";
  }
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return "utf-8";
  } catch {
    return "windows-1256";
  }
}
function decodeCsv(bytes) {
  const encoding = detectEncoding(bytes);
  const text = new TextDecoder(encoding).decode(bytes);
  return text.charCodeAt(0) === 65279 ? text.slice(1) : text;
}
function parseCsv(text) {
  const firstLine = text.slice(0, text.indexOf("\n") === -1 ? text.length : text.indexOf("\n"));
  const semicolons = (firstLine.match(/;/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;
  const delimiter = semicolons > commas ? ";" : ",";
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      row.push(field);
      field = "";
      continue;
    }
    if (ch === "\r") continue;
    if (ch === "\n") {
      row.push(field);
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
      field = "";
      continue;
    }
    field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}
var COLUMN_ALIASES = {
  nameAr: ["\u0627\u0644\u0627\u0633\u0645", "\u0627\u0633\u0645 \u0627\u0644\u0645\u0646\u062A\u062C", "\u0627\u0644\u0645\u0646\u062A\u062C", "\u0627\u0644\u062A\u0633\u0645\u064A\u0629", "name", "nom", "designation", "produit"],
  nameFr: ["\u0627\u0644\u0627\u0633\u0645 \u0628\u0627\u0644\u0641\u0631\u0646\u0633\u064A\u0629", "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0641\u0631\u0646\u0633\u064A", "name_fr", "nom_fr"],
  sku: ["\u0627\u0644\u0631\u0645\u0632", "\u0631\u0645\u0632 \u0627\u0644\u0645\u0646\u062A\u062C", "\u0627\u0644\u0645\u0631\u062C\u0639", "sku", "code", "reference", "ref"],
  barcode: ["\u0627\u0644\u0628\u0627\u0631\u0643\u0648\u062F", "\u0628\u0627\u0631\u0643\u0648\u062F", "\u0631\u0645\u0632 \u0634\u0631\u064A\u0637\u064A", "barcode", "ean", "code_barre", "gencod"],
  category: ["\u0627\u0644\u062A\u0635\u0646\u064A\u0641", "\u0627\u0644\u0635\u0646\u0641", "\u0627\u0644\u0641\u0626\u0629", "category", "categorie", "famille"],
  type: ["\u0627\u0644\u0646\u0648\u0639", "type"],
  unit: ["\u0627\u0644\u0648\u062D\u062F\u0629", "\u0648\u062D\u062F\u0629", "unit", "unite"],
  costPrice: ["\u0633\u0639\u0631 \u0627\u0644\u062A\u0643\u0644\u0641\u0629", "\u0627\u0644\u062A\u0643\u0644\u0641\u0629", "\u0633\u0639\u0631 \u0627\u0644\u0634\u0631\u0627\u0621", "\u062B\u0645\u0646 \u0627\u0644\u0634\u0631\u0627\u0621", "cost", "prix_achat", "achat", "pa"],
  retailPrice: ["\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639", "\u0627\u0644\u0633\u0639\u0631", "\u062B\u0645\u0646 \u0627\u0644\u0628\u064A\u0639", "price", "prix", "prix_vente", "vente", "pv"],
  wholesalePrice: ["\u0633\u0639\u0631 \u0627\u0644\u062C\u0645\u0644\u0629", "\u0627\u0644\u062C\u0645\u0644\u0629", "wholesale", "prix_gros", "gros"],
  taxRate: ["\u0627\u0644\u0636\u0631\u064A\u0628\u0629", "\u0646\u0633\u0628\u0629 \u0627\u0644\u0636\u0631\u064A\u0628\u0629", "tva", "tax"],
  quantity: ["\u0627\u0644\u0643\u0645\u064A\u0629", "\u0627\u0644\u0631\u0635\u064A\u062F", "\u0627\u0644\u0645\u062E\u0632\u0648\u0646", "quantity", "qty", "quantite", "stock"],
  minStock: ["\u062D\u062F \u0627\u0644\u062A\u0646\u0628\u064A\u0647", "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u062F\u0646\u0649", "\u0623\u0642\u0644 \u0643\u0645\u064A\u0629", "min_stock", "minimum", "seuil"],
  isbn: ["isbn", "\u0631\u062F\u0645\u0643"],
  author: ["\u0627\u0644\u0645\u0624\u0644\u0641", "\u0627\u0644\u0643\u0627\u062A\u0628", "author", "auteur"],
  publisher: ["\u0627\u0644\u0646\u0627\u0634\u0631", "\u062F\u0627\u0631 \u0627\u0644\u0646\u0634\u0631", "publisher", "editeur"]
};
function normalizeHeader(raw) {
  return raw.trim().toLowerCase().replace(/[ً-ٰٟـ]/g, "").replace(/[أإآٱ]/g, "\u0627").replace(/ة/g, "\u0647").replace(/ى/g, "\u064A").replace(/[\s_\-.]+/g, "");
}
function mapHeaders(headers) {
  const mapping = {};
  const normalized = headers.map(normalizeHeader);
  for (const [column, aliases] of Object.entries(COLUMN_ALIASES)) {
    const index = normalized.findIndex((h) => aliases.some((a) => normalizeHeader(a) === h));
    if (index !== -1) mapping[column] = index;
  }
  return mapping;
}
function parseNumber(raw) {
  if (raw == null) return null;
  const cleaned = normalizeDigits(String(raw)).replace(/[\s ']/g, "").replace(/[٬،]/g, "");
  if (cleaned === "") return null;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized = cleaned;
  if (lastComma !== -1 && lastDot !== -1) {
    normalized = lastComma > lastDot ? cleaned.replace(/\./g, "").replace(",", ".") : cleaned.replace(/,/g, "");
  } else if (lastComma !== -1) {
    normalized = cleaned.length - lastComma <= 3 ? cleaned.replace(",", ".") : cleaned.replace(/,/g, "");
  }
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}
var TYPE_ALIASES = {
  \u0643\u062A\u0627\u0628: "BOOK",
  \u0643\u062A\u0628: "BOOK",
  book: "BOOK",
  livre: "BOOK",
  \u0642\u0631\u0637\u0627\u0633\u064A\u0629: "STATIONERY",
  \u0627\u062F\u0648\u0627\u062A: "STATIONERY",
  stationery: "STATIONERY",
  fourniture: "STATIONERY",
  \u062E\u062F\u0645\u0629: "SERVICE",
  \u062E\u062F\u0645\u0627\u062A: "SERVICE",
  service: "SERVICE"
};
var importOptionsSchema = z7.object({
  /** إنشاء التصنيفات غير الموجودة بدل رفض الصفّ */
  createCategories: z7.boolean().default(true),
  /** تحديث المنتجات الموجودة بدل تخطّيها */
  updateExisting: z7.boolean().default(true),
  /** استيراد الكميات كرصيد افتتاحي */
  importQuantities: z7.boolean().default(true)
});
var REQUIRED_COLUMNS = ["nameAr", "retailPrice"];
function buildImportPreview(rows, existing, knownCategories, options) {
  const [headerRow, ...dataRows] = rows;
  if (!headerRow) {
    return {
      totalRows: 0,
      toCreate: 0,
      toUpdate: 0,
      errorCount: 0,
      unmappedHeaders: [],
      missingRequired: REQUIRED_COLUMNS.map(String),
      rows: [],
      newCategories: []
    };
  }
  const mapping = mapHeaders(headerRow);
  const missingRequired = REQUIRED_COLUMNS.filter((c) => mapping[c] === void 0);
  const mappedIndexes = new Set(Object.values(mapping));
  const unmappedHeaders = headerRow.map((h, i) => ({ h: h.trim(), i })).filter((x) => x.h !== "" && !mappedIndexes.has(x.i)).map((x) => x.h);
  const bySku = new Map(existing.map((p) => [p.sku.toLowerCase(), p]));
  const byBarcode = new Map(existing.filter((p) => p.barcode).map((p) => [p.barcode, p]));
  const knownCategorySet = new Set(knownCategories.map((c) => c.trim()));
  const seenSku = /* @__PURE__ */ new Map();
  const seenBarcode = /* @__PURE__ */ new Map();
  const newCategories = /* @__PURE__ */ new Set();
  const results = [];
  for (const [index, row] of dataRows.entries()) {
    const line = index + 2;
    const errors = [];
    const warnings = [];
    const cell = (c) => {
      const i = mapping[c];
      return i === void 0 ? "" : (row[i] ?? "").trim();
    };
    if (missingRequired.length) {
      results.push({ line, action: "error", errors: ["\u0623\u0639\u0645\u062F\u0629 \u0645\u0637\u0644\u0648\u0628\u0629 \u0646\u0627\u0642\u0635\u0629 \u0641\u064A \u0627\u0644\u0645\u0644\u0641"], warnings: [] });
      continue;
    }
    const nameAr = cell("nameAr");
    if (!nameAr) errors.push("\u0627\u0644\u0627\u0633\u0645 \u0641\u0627\u0631\u063A");
    if (nameAr.length > 200) errors.push("\u0627\u0644\u0627\u0633\u0645 \u0623\u0637\u0648\u0644 \u0645\u0646 \u0662\u0660\u0660 \u062D\u0631\u0641");
    const retailRaw = parseNumber(cell("retailPrice"));
    if (retailRaw === null) errors.push("\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D");
    else if (retailRaw < 0) errors.push("\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639 \u0633\u0627\u0644\u0628");
    const costRaw = parseNumber(cell("costPrice")) ?? 0;
    if (costRaw < 0) errors.push("\u0633\u0639\u0631 \u0627\u0644\u062A\u0643\u0644\u0641\u0629 \u0633\u0627\u0644\u0628");
    if (retailRaw !== null && retailRaw > 0 && costRaw > retailRaw) {
      warnings.push("\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639 \u0623\u0642\u0644\u0651 \u0645\u0646 \u0627\u0644\u062A\u0643\u0644\u0641\u0629");
    }
    const barcodeRaw = normalizeDigits(cell("barcode")).replace(/\s/g, "");
    const barcode = barcodeRaw || null;
    if (barcode && !/^[0-9A-Za-z-]+$/.test(barcode)) errors.push("\u0627\u0644\u0628\u0627\u0631\u0643\u0648\u062F \u064A\u062D\u0648\u064A \u0631\u0645\u0648\u0632\u0627\u064B \u063A\u064A\u0631 \u0645\u0642\u0628\u0648\u0644\u0629");
    const quantity = options.importQuantities ? Math.max(0, Math.round(parseNumber(cell("quantity")) ?? 0)) : 0;
    const minStock = Math.max(0, Math.round(parseNumber(cell("minStock")) ?? 0));
    const typeRaw = normalizeHeader(cell("type"));
    const isbn = normalizeDigits(cell("isbn")).replace(/[\s-]/g, "") || null;
    const type = TYPE_ALIASES[typeRaw] ?? (isbn || cell("author") ? "BOOK" : "STATIONERY");
    const categoryName = cell("category") || null;
    if (categoryName && !knownCategorySet.has(categoryName)) {
      if (options.createCategories) newCategories.add(categoryName);
      else errors.push(`\u0627\u0644\u062A\u0635\u0646\u064A\u0641 \xAB${categoryName}\xBB \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F`);
    }
    const skuCell = cell("sku");
    const matched = skuCell && bySku.get(skuCell.toLowerCase()) || barcode && byBarcode.get(barcode) || null;
    if (skuCell) {
      const prev = seenSku.get(skuCell.toLowerCase());
      if (prev) errors.push(`\u0627\u0644\u0631\u0645\u0632 \u0645\u0643\u0631\u0651\u0631 \u0645\u0639 \u0627\u0644\u0633\u0637\u0631 ${prev}`);
      else seenSku.set(skuCell.toLowerCase(), line);
    }
    if (barcode) {
      const prev = seenBarcode.get(barcode);
      if (prev) errors.push(`\u0627\u0644\u0628\u0627\u0631\u0643\u0648\u062F \u0645\u0643\u0631\u0651\u0631 \u0645\u0639 \u0627\u0644\u0633\u0637\u0631 ${prev}`);
      else seenBarcode.set(barcode, line);
    }
    if (matched && !options.updateExisting) {
      results.push({ line, action: "error", errors: ["\u0627\u0644\u0645\u0646\u062A\u062C \u0645\u0648\u062C\u0648\u062F \u0648\u0627\u0644\u062A\u062D\u062F\u064A\u062B \u0645\u064F\u0639\u0637\u064E\u0651\u0644"], warnings });
      continue;
    }
    if (errors.length) {
      results.push({ line, action: "error", errors, warnings });
      continue;
    }
    const taxRate = Math.max(0, Math.min(100, Math.round(parseNumber(cell("taxRate")) ?? 0)));
    const wholesale = parseNumber(cell("wholesalePrice"));
    results.push({
      line,
      action: matched ? "update" : "create",
      errors: [],
      warnings,
      existingId: matched?.id,
      data: {
        // رمز مُولَّد من رقم السطر عند غيابه — فريد وقابل للتتبّع إلى الملف
        sku: skuCell || `IMP-${String(line).padStart(5, "0")}`,
        barcode,
        nameAr,
        nameFr: cell("nameFr") || null,
        categoryName,
        type,
        unit: cell("unit") || "\u0642\u0637\u0639\u0629",
        costPrice: toMinor(costRaw),
        retailPrice: toMinor(retailRaw ?? 0),
        wholesalePrice: wholesale !== null ? toMinor(wholesale) : null,
        taxRate,
        quantity,
        minStock,
        isbn,
        author: cell("author") || null,
        publisher: cell("publisher") || null
      }
    });
  }
  return {
    totalRows: dataRows.length,
    toCreate: results.filter((r) => r.action === "create").length,
    toUpdate: results.filter((r) => r.action === "update").length,
    errorCount: results.filter((r) => r.action === "error").length,
    unmappedHeaders,
    missingRequired,
    rows: results,
    newCategories: [...newCategories]
  };
}
var IMPORT_TEMPLATE_HEADERS = [
  "\u0627\u0644\u0627\u0633\u0645",
  "\u0627\u0644\u0631\u0645\u0632",
  "\u0627\u0644\u0628\u0627\u0631\u0643\u0648\u062F",
  "\u0627\u0644\u062A\u0635\u0646\u064A\u0641",
  "\u0627\u0644\u0646\u0648\u0639",
  "\u0627\u0644\u0648\u062D\u062F\u0629",
  "\u0633\u0639\u0631 \u0627\u0644\u062A\u0643\u0644\u0641\u0629",
  "\u0633\u0639\u0631 \u0627\u0644\u0628\u064A\u0639",
  "\u0627\u0644\u0643\u0645\u064A\u0629",
  "\u062D\u062F \u0627\u0644\u062A\u0646\u0628\u064A\u0647",
  "ISBN",
  "\u0627\u0644\u0645\u0624\u0644\u0641",
  "\u0627\u0644\u0646\u0627\u0634\u0631"
];
var IMPORT_TEMPLATE_SAMPLE = [
  ["\u0627\u0644\u0631\u064A\u0627\u0636\u064A\u0627\u062A \u2014 \u0627\u0644\u0633\u0646\u0629 \u0627\u0644\u0623\u0648\u0644\u0649 \u0645\u062A\u0648\u0633\u0637", "", "9789947850012", "\u0643\u062A\u0628 \u0645\u062F\u0631\u0633\u064A\u0629", "\u0643\u062A\u0627\u0628", "\u0642\u0637\u0639\u0629", "320", "450", "45", "10", "9789947850012", "", "\u0627\u0644\u062F\u064A\u0648\u0627\u0646 \u0627\u0644\u0648\u0637\u0646\u064A"],
  ["\u0642\u0644\u0645 \u062C\u0627\u0641 \u0623\u0632\u0631\u0642", "QRT-001", "3086123456789", "\u0623\u0642\u0644\u0627\u0645", "\u0642\u0631\u0637\u0627\u0633\u064A\u0629", "\u0642\u0637\u0639\u0629", "25", "40", "480", "100", "", "", ""]
];

// src/customers.ts
import { z as z8 } from "zod";
var customerSchema = z8.object({
  phone: z8.string().trim().min(9, "\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641 \u0642\u0635\u064A\u0631 \u062C\u062F\u0627\u064B").max(32).regex(/^[0-9+\s-]+$/, "\u0631\u0642\u0645 \u0647\u0627\u062A\u0641 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D"),
  fullName: z8.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0632\u0628\u0648\u0646 \u0645\u0637\u0644\u0648\u0628").max(120),
  email: z8.email("\u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u063A\u064A\u0631 \u0635\u0627\u0644\u062D").max(160).optional().or(z8.literal("")),
  address: z8.string().trim().max(255).optional().or(z8.literal("")),
  notes: z8.string().trim().max(1e3).optional().or(z8.literal("")),
  /** دين قائم قبل دخول النظام */
  openingBalance: z8.coerce.number().int().min(0).default(0),
  /** سقف الدين — صفر يعني بلا سقف */
  creditLimit: z8.coerce.number().int().min(0).default(0),
  isActive: z8.boolean().default(true)
});
var customerPaymentSchema = z8.object({
  customerId: z8.uuid("\u0627\u062E\u062A\u0631 \u0632\u0628\u0648\u0646\u0627\u064B"),
  saleId: z8.uuid().nullable().optional(),
  method: z8.enum(["CASH", "CARD", "TRANSFER"]).default("CASH"),
  amount: z8.coerce.number().int().min(1, "\u0627\u0644\u0645\u0628\u0644\u063A \u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0623\u0643\u0628\u0631 \u0645\u0646 \u0635\u0641\u0631"),
  docNumber: z8.string().trim().max(64).optional().or(z8.literal("")),
  note: z8.string().trim().max(255).optional().or(z8.literal("")),
  /** إضافة المبلغ إلى درج الصندوق إن كان نقداً ووردية مفتوحة */
  toDrawer: z8.boolean().default(false),
  paidAt: z8.string().optional()
});
function exceedsCreditLimit(currentBalance, creditLimit, newDebt) {
  if (creditLimit <= 0) return false;
  return currentBalance + newDebt > creditLimit;
}

// src/settings.ts
import { z as z9 } from "zod";
var settingsSchema = z9.object({
  /* ── بيانات المكتبة (تظهر على الفواتير) ── */
  "store.name": z9.string().trim().min(1, "\u0627\u0633\u0645 \u0627\u0644\u0645\u0643\u062A\u0628\u0629 \u0645\u0637\u0644\u0648\u0628").max(120).default("\u0645\u0643\u062A\u0628\u0629 \u0627\u0644\u0646\u062C\u0627\u062D"),
  "store.phone": z9.string().trim().max(32).default(""),
  "store.address": z9.string().trim().max(255).default(""),
  /** رقم التعريف الجبائي / السجل التجاري — يُطلب على الفواتير الرسمية */
  "store.taxId": z9.string().trim().max(48).default(""),
  "store.rc": z9.string().trim().max(48).default(""),
  /* ── العملة والأرقام ── */
  "currency.code": z9.enum(["DZD", "MAD", "TND", "EUR", "USD"]).default("DZD"),
  "locale.digits": z9.enum(["latn", "arab"]).default("latn"),
  /* ── الضريبة ── */
  "tax.enabled": z9.boolean().default(false),
  "tax.defaultRate": z9.coerce.number().int().min(0).max(100).default(0),
  /* ── المخزون ── */
  "inventory.lowStockThreshold": z9.coerce.number().int().min(0).max(1e4).default(5),
  /** السماح بالبيع المباشر تحت الصفر — المزامنة تقبله دائماً بحكم الواقع */
  "inventory.allowNegative": z9.boolean().default(false),
  /* ── الفاتورة ── */
  "invoice.footer": z9.string().trim().max(255).default("\u0634\u0643\u0631\u0627\u064B \u0644\u062A\u0639\u0627\u0645\u0644\u0643\u0645 \u0645\u0639\u0646\u0627"),
  "invoice.showTax": z9.boolean().default(false),
  /* ── نقطة البيع ── */
  "pos.printAfterSale": z9.boolean().default(true)
});
var settingsUpdateSchema = settingsSchema.partial();
function defaultSettings() {
  return settingsSchema.parse({});
}
function mergeSettings(stored) {
  const defaults = defaultSettings();
  const result = { ...defaults };
  for (const [key, value] of Object.entries(stored)) {
    if (!(key in defaults)) continue;
    const field = settingsSchema.shape[key];
    const parsed = field.safeParse(value);
    if (parsed.success) result[key] = parsed.data;
  }
  return result;
}
var SETTINGS_GROUPS = [
  {
    key: "store",
    title: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0643\u062A\u0628\u0629",
    description: "\u062A\u0638\u0647\u0631 \u0639\u0644\u0649 \u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631 \u0648\u0627\u0644\u0625\u064A\u0635\u0627\u0644\u0627\u062A",
    fields: ["store.name", "store.phone", "store.address", "store.taxId", "store.rc"]
  },
  {
    key: "money",
    title: "\u0627\u0644\u0639\u0645\u0644\u0629 \u0648\u0627\u0644\u0636\u0631\u064A\u0628\u0629",
    description: "\u062A\u0624\u062B\u0651\u0631 \u0639\u0644\u0649 \u0643\u0644 \u0627\u0644\u0645\u0628\u0627\u0644\u063A \u0641\u064A \u0627\u0644\u0646\u0638\u0627\u0645",
    fields: ["currency.code", "locale.digits", "tax.enabled", "tax.defaultRate"]
  },
  {
    key: "inventory",
    title: "\u0627\u0644\u0645\u062E\u0632\u0648\u0646",
    description: "\u062D\u062F\u0648\u062F \u0627\u0644\u062A\u0646\u0628\u064A\u0647 \u0648\u0633\u0644\u0648\u0643 \u0627\u0644\u0628\u064A\u0639",
    fields: ["inventory.lowStockThreshold", "inventory.allowNegative"]
  },
  {
    key: "invoice",
    title: "\u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0648\u0646\u0642\u0637\u0629 \u0627\u0644\u0628\u064A\u0639",
    description: "\u0634\u0643\u0644 \u0627\u0644\u0625\u064A\u0635\u0627\u0644 \u0648\u0633\u0644\u0648\u0643 \u0627\u0644\u0628\u064A\u0639",
    fields: ["invoice.footer", "invoice.showTax", "pos.printAfterSale"]
  }
];
var SETTING_LABELS = {
  "store.name": { label: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0643\u062A\u0628\u0629", hint: "\u064A\u0638\u0647\u0631 \u0623\u0639\u0644\u0649 \u0643\u0644 \u0625\u064A\u0635\u0627\u0644" },
  "store.phone": { label: "\u0627\u0644\u0647\u0627\u062A\u0641" },
  "store.address": { label: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646" },
  "store.taxId": { label: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u062C\u0628\u0627\u0626\u064A (NIF)", hint: "\u0627\u062A\u0631\u0643\u0647 \u0641\u0627\u0631\u063A\u0627\u064B \u0625\u0646 \u0644\u0645 \u064A\u0643\u0646 \u0645\u0637\u0644\u0648\u0628\u0627\u064B" },
  "store.rc": { label: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A (RC)" },
  "currency.code": { label: "\u0627\u0644\u0639\u0645\u0644\u0629" },
  "locale.digits": { label: "\u0634\u0643\u0644 \u0627\u0644\u0623\u0631\u0642\u0627\u0645", hint: "\u063A\u0631\u0628\u064A\u0629 0-9 \u0623\u0648 \u0647\u0646\u062F\u064A\u0629 \u0660-\u0669" },
  "tax.enabled": { label: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0636\u0631\u064A\u0628\u0629", hint: "\u064A\u064F\u0638\u0647\u0631 \u062D\u0642\u0648\u0644 \u0627\u0644\u0636\u0631\u064A\u0628\u0629 \u0641\u064A \u0627\u0644\u0645\u0646\u062A\u062C\u0627\u062A \u0648\u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631" },
  "tax.defaultRate": { label: "\u0646\u0633\u0628\u0629 \u0627\u0644\u0636\u0631\u064A\u0628\u0629 \u0627\u0644\u0627\u0641\u062A\u0631\u0627\u0636\u064A\u0629", hint: "\u062A\u064F\u0642\u062A\u0631\u062D \u0639\u0646\u062F \u0625\u0636\u0627\u0641\u0629 \u0645\u0646\u062A\u062C \u062C\u062F\u064A\u062F" },
  "inventory.lowStockThreshold": { label: "\u062D\u062F\u0651 \u0627\u0644\u062A\u0646\u0628\u064A\u0647 \u0627\u0644\u0627\u0641\u062A\u0631\u0627\u0636\u064A", hint: "\u064A\u064F\u0642\u062A\u0631\u062D \u0639\u0646\u062F \u0625\u0636\u0627\u0641\u0629 \u0645\u0646\u062A\u062C" },
  "inventory.allowNegative": {
    label: "\u0627\u0644\u0633\u0645\u0627\u062D \u0628\u0627\u0644\u0628\u064A\u0639 \u062A\u062D\u062A \u0627\u0644\u0635\u0641\u0631",
    hint: "\u0627\u0644\u0628\u064A\u0639 \u062F\u0648\u0646 \u0627\u062A\u0635\u0627\u0644 \u064A\u064F\u0642\u0628\u0644 \u062F\u0627\u0626\u0645\u0627\u064B\u061B \u0647\u0630\u0627 \u064A\u062E\u0635\u0651 \u0627\u0644\u0628\u064A\u0639 \u0627\u0644\u0645\u062A\u0651\u0635\u0644 \u0641\u0642\u0637"
  },
  "invoice.footer": { label: "\u062A\u0630\u064A\u064A\u0644 \u0627\u0644\u0625\u064A\u0635\u0627\u0644" },
  "invoice.showTax": { label: "\u0625\u0638\u0647\u0627\u0631 \u0627\u0644\u0636\u0631\u064A\u0628\u0629 \u0639\u0644\u0649 \u0627\u0644\u0625\u064A\u0635\u0627\u0644" },
  "pos.printAfterSale": { label: "\u0641\u062A\u062D \u0646\u0627\u0641\u0630\u0629 \u0627\u0644\u0637\u0628\u0627\u0639\u0629 \u0628\u0639\u062F \u0627\u0644\u0628\u064A\u0639" }
};

// src/stores.ts
import { z as z10 } from "zod";
var storeSchema = z10.object({
  code: z10.string().trim().min(1, "\u0631\u0645\u0632 \u0627\u0644\u0641\u0631\u0639 \u0645\u0637\u0644\u0648\u0628").max(16).regex(/^[A-Z0-9-]+$/, "\u0623\u062D\u0631\u0641 \u0644\u0627\u062A\u064A\u0646\u064A\u0629 \u0643\u0628\u064A\u0631\u0629 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648 - \u0641\u0642\u0637"),
  nameAr: z10.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0641\u0631\u0639 \u0645\u0637\u0644\u0648\u0628").max(120),
  address: z10.string().trim().max(255).optional().or(z10.literal("")),
  phone: z10.string().trim().max(32).optional().or(z10.literal("")),
  isActive: z10.boolean().default(true)
});
var storeUpdateSchema = storeSchema.partial().omit({ code: true });
var registerSchema = z10.object({
  storeId: z10.string().uuid("\u0627\u062E\u062A\u0631 \u0627\u0644\u0641\u0631\u0639"),
  code: z10.string().trim().min(1, "\u0631\u0645\u0632 \u0627\u0644\u0635\u0646\u062F\u0648\u0642 \u0645\u0637\u0644\u0648\u0628").max(16).regex(/^[A-Z0-9-]+$/, "\u0623\u062D\u0631\u0641 \u0644\u0627\u062A\u064A\u0646\u064A\u0629 \u0643\u0628\u064A\u0631\u0629 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648 - \u0641\u0642\u0637"),
  nameAr: z10.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u0635\u0646\u062F\u0648\u0642 \u0645\u0637\u0644\u0648\u0628").max(80),
  isActive: z10.boolean().default(true)
});
var registerUpdateSchema = registerSchema.partial().omit({ storeId: true, code: true });
var deviceUpdateSchema = z10.object({
  nameAr: z10.string().trim().min(2, "\u0627\u0633\u0645 \u0627\u0644\u062C\u0647\u0627\u0632 \u0645\u0637\u0644\u0648\u0628").max(120).optional(),
  storeId: z10.string().uuid().nullable().optional(),
  isActive: z10.boolean().optional()
});

// src/media.ts
function thumbUrl(url) {
  if (!url) return null;
  return url.endsWith(".webp") && !url.endsWith("_thumb.webp") ? url.replace(/\.webp$/, "_thumb.webp") : url;
}
function primaryImage(images) {
  return images?.[0] ?? null;
}

// src/stocktake.ts
import { z as z11 } from "zod";
var STOCK_TAKE_STATUS = {
  DRAFT: "\u0645\u0633\u0648\u0651\u062F\u0629",
  COUNTING: "\u062C\u0627\u0631\u064D \u0627\u0644\u0639\u062F\u0651",
  COMPLETED: "\u0645\u0643\u062A\u0645\u0644",
  CANCELLED: "\u0645\u0644\u063A\u0649"
};
var stockTakeScopeSchema = z11.enum(["ALL", "CATEGORY", "LOW_STOCK"]);
var STOCK_TAKE_SCOPES = [
  { value: "ALL", label: "\u0643\u0644 \u0627\u0644\u0623\u0635\u0646\u0627\u0641", hint: "\u062C\u0631\u062F \u0634\u0627\u0645\u0644 \u2014 \u0627\u0644\u0623\u0635\u0646\u0627\u0641 \u063A\u064A\u0631 \u0627\u0644\u0645\u0639\u062F\u0648\u062F\u0629 \u062A\u064F\u0639\u062F\u0651 \u0635\u0641\u0631\u0627\u064B \u0639\u0646\u062F \u0627\u0644\u0625\u0642\u0641\u0627\u0644" },
  { value: "CATEGORY", label: "\u062A\u0635\u0646\u064A\u0641 \u0648\u0627\u062D\u062F", hint: "\u062C\u0631\u062F \u062C\u0632\u0626\u064A \u0644\u0631\u0641\u0651 \u0623\u0648 \u0642\u0633\u0645" },
  { value: "LOW_STOCK", label: "\u0645\u0627 \u062F\u0648\u0646 \u062D\u062F\u0651 \u0627\u0644\u062A\u0646\u0628\u064A\u0647", hint: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0633\u0631\u064A\u0639\u0629 \u0644\u0645\u0627 \u064A\u064F\u0634\u062A\u0628\u0647 \u0641\u064A \u0646\u0641\u0627\u062F\u0647" }
];
var createStockTakeSchema = z11.object({
  scope: stockTakeScopeSchema.default("ALL"),
  categoryId: z11.uuid().nullable().optional(),
  storeId: z11.uuid().optional(),
  note: z11.string().trim().max(255).optional().or(z11.literal(""))
}).refine((v) => v.scope !== "CATEGORY" || !!v.categoryId, {
  message: "\u0627\u062E\u062A\u0631 \u0627\u0644\u062A\u0635\u0646\u064A\u0641",
  path: ["categoryId"]
});
var stockTakeCountSchema = z11.object({
  counts: z11.array(z11.object({
    productId: z11.uuid(),
    /** فارغ = لم يُعدّ بعد؛ يختلف عن صفر الذي يعني «لا شيء على الرفّ» */
    countedQty: z11.coerce.number().int().min(0).max(1e6).nullable()
  })).min(1, "\u0644\u0627 \u0639\u062F\u0651 \u0644\u0625\u0631\u0633\u0627\u0644\u0647").max(500, "\u0623\u0631\u0633\u0644 \u0665\u0660\u0660 \u0635\u0646\u0641 \u0643\u062D\u062F\u0651 \u0623\u0642\u0635\u0649 \u0641\u064A \u0627\u0644\u062F\u0641\u0639\u0629")
});
var completeStockTakeSchema = z11.object({
  /**
   * الأصناف التي لم تُعدّ: `SKIP` تتركها على رصيدها الدفتري،
   * `ZERO` تعتبرها صفراً. الثانية صحيحة في الجرد الشامل وحده،
   * وكارثية في الجزئي — ولذلك الاختيار صريح لا افتراضي.
   */
  uncounted: z11.enum(["SKIP", "ZERO"]).default("SKIP"),
  note: z11.string().trim().max(255).optional().or(z11.literal(""))
});

// src/notifications.ts
import { z as z12 } from "zod";
var pushTokenSchema = z12.object({
  token: z12.string().trim().min(20, "\u0631\u0645\u0632 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u063A\u064A\u0631 \u0635\u0627\u0644\u062D").max(2048).regex(/^[A-Za-z0-9_:.-]+$/),
  platform: z12.literal("android")
});
var removePushTokenSchema = pushTokenSchema.pick({ token: true });
var ORDER_NOTIFICATION_CHANNEL = "order-updates";
export {
  ALL_PERMISSIONS,
  CASH_MOVEMENT_TYPES,
  CURRENCIES,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  FULFILLMENT_TYPES,
  GRANULARITIES,
  IMPORT_TEMPLATE_HEADERS,
  IMPORT_TEMPLATE_SAMPLE,
  MANUAL_MOVEMENT_REASONS,
  MOVEMENT_REASONS,
  ORDER_NOTIFICATION_CHANNEL,
  ORDER_STATUSES,
  ORDER_TRANSITIONS,
  PAYMENT_DIRECTIONS,
  PAYMENT_METHODS,
  PERMISSIONS,
  PRODUCT_TYPES,
  PURCHASE_STATUSES,
  REPORT_PERIODS,
  ROLE_PRESETS,
  SALE_STATUSES,
  SETTINGS_GROUPS,
  SETTING_LABELS,
  SHIFT_STATUSES,
  STOCK_TAKE_SCOPES,
  STOCK_TAKE_STATUS,
  TAX_RATES,
  UNITS,
  addressSchema,
  allocateShipping,
  applyProductRules,
  authorSchema,
  bookDetailSchema,
  bucketLabel,
  buildImportPreview,
  buildOrderReference,
  buildSaleReference,
  buildSearchKey,
  calcChange,
  calcDeliveryFee,
  calcLine,
  calcPurchaseTotals,
  calcSaleLine,
  calcSaleTotals,
  canTransition,
  cashMovementSchema,
  categorySchema,
  changePasswordSchema,
  closeShiftSchema,
  completeStockTakeSchema,
  createProductSchema,
  createRoleSchema,
  createStockTakeSchema,
  createUserSchema,
  customerCanCancel,
  customerLoginSchema,
  customerPaymentSchema,
  customerRegisterSchema,
  customerSchema,
  decodeCsv,
  defaultSettings,
  deliveryZoneSchema,
  detectEncoding,
  deviceUpdateSchema,
  exceedsCreditLimit,
  expenseCategorySchema,
  expenseSchema,
  formatAddress,
  formatCompact,
  formatDate,
  formatDateTime,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  hasAnyPermission,
  hasPermission,
  importOptionsSchema,
  isTerminal,
  landedUnitCost,
  loginSchema,
  mapHeaders,
  marginOf,
  mergeSettings,
  movementFilterSchema,
  normalizeArabic,
  normalizeDigits,
  openShiftSchema,
  orderItemSchema,
  paginationSchema,
  parseCsv,
  parseNumber,
  permissionSchema,
  placeOrderSchema,
  primaryImage,
  productBaseSchema,
  productFilterSchema,
  productSchema,
  profitMargin,
  publisherSchema,
  purchaseItemSchema,
  purchaseSchema,
  pushTokenSchema,
  refreshSchema,
  registerSchema,
  registerUpdateSchema,
  removePushTokenSchema,
  reportRangeSchema,
  resolveRange,
  saleItemSchema,
  salePaymentSchema,
  saleReturnSchema,
  saleSchema,
  settingsSchema,
  settingsUpdateSchema,
  stockAdjustmentSchema,
  stockCountSchema,
  stockStatus,
  stockTakeCountSchema,
  stockTakeScopeSchema,
  storeSchema,
  storeUpdateSchema,
  suggestSku,
  sumTotals,
  supplierPaymentSchema,
  supplierSchema,
  thumbUrl,
  toCsv,
  toMajor,
  toMinor,
  updateOrderStatusSchema,
  updateUserSchema,
  weightedAverageCost
};
//# sourceMappingURL=index.js.map